import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import SiteHeader from "@/components/SiteHeader";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import HowItWorksSteps from "@/components/HowItWorksSteps";
import {
  Home, ChevronRight, Calendar, Euro, Shield, Headphones,
  Check, ArrowRight,
  CalendarRange, Ticket, Percent, QrCode, ShieldCheck as Trust, LifeBuoy
} from "lucide-react";

const CF_IMAGES = {
  hero:  "https://customer-assets.emergentagent.com/job_f049ee2b-edae-4cc4-8e76-bfc796e90f9f/artifacts/iolb4qzd_banner-como-funciona.png",
};

export default function ComoFuncionaPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const handleBuy = () => navigate("/#passport");

  const HERO_BENEFITS = [
    { Icon: Calendar,   title: t("cf.b1t"), sub: t("cf.b1s") },
    { Icon: Euro,       title: t("cf.b2t"), sub: t("cf.b2s") },
    { Icon: Shield,     title: t("cf.b3t"), sub: t("cf.b3s") },
    { Icon: Headphones, title: t("cf.b4t"), sub: t("cf.b4s") },
  ];

  const ADVANTAGES = [
    { Icon: CalendarRange, title: t("cf.a1t"), desc: t("cf.a1d") },
    { Icon: Ticket,        title: t("cf.a2t"), desc: t("cf.a2d") },
    { Icon: Percent,       title: t("cf.a3t"), desc: t("cf.a3d") },
    { Icon: QrCode,        title: t("cf.a4t"), desc: t("cf.a4d") },
    { Icon: Trust,         title: t("cf.a5t"), desc: t("cf.a5d") },
    { Icon: LifeBuoy,      title: t("cf.a6t"), desc: t("cf.a6d") },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif" }}>
      <Seo
        title={language === "en"
          ? "How PASS Algarve Works | Buy, Show the QR Code and Enjoy"
          : "Como Funciona o PASS Algarve | Compre, Apresente o QR Code e Aproveite"}
        description={language === "en"
          ? "Learn how PASS Algarve works: choose your pass, receive your digital QR Code and enjoy the best attractions and experiences in the Algarve with a single ticket."
          : "Saiba como funciona o PASS Algarve: escolha o seu passe, receba o QR Code digital e aproveite as melhores atrações e experiências do Algarve com um único bilhete."}
      />
      <SiteHeader active={t("nav.how")} onBuyClick={handleBuy} />

      {/* ============== HERO ============== */}
      <section
        className="relative pt-20 bg-[#071A35] overflow-hidden"
        data-testid="cf-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${CF_IMAGES.hero})` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, #071A35 0%, rgba(7,26,53,0.92) 30%, rgba(7,26,53,0.55) 55%, rgba(7,26,53,0.15) 80%, rgba(7,26,53,0) 100%)",
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 min-h-[520px] flex items-center">
          <div className="text-white max-w-xl">
            <nav className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 mb-7" data-testid="cf-breadcrumb">
              <Link to="/" className="flex items-center gap-1 hover:text-[#D4A85D]"><Home className="w-3 h-3" /> {t("cf.home")}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-[#D4A85D]">{t("cf.breadcrumb")}</span>
            </nav>

            <p className="text-[#D4A85D] text-xs uppercase tracking-[0.25em] font-bold mb-3">{t("cf.label")}</p>
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-5xl lg:text-6xl text-white leading-[1.05] mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("cf.title1")}<span className="italic text-[#D4A85D]">{t("cf.title2")}</span>
            </motion.h1>
            <p className="text-white/85 text-base lg:text-lg leading-relaxed max-w-md mb-2">
              {t("cf.subtitle")}
            </p>
            <p className="text-[#D4A85D] text-base font-medium mb-10">{t("cf.subtitle2")}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
              {HERO_BENEFITS.map((b, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 mx-auto rounded-full border border-[#D4A85D]/40 bg-[#D4A85D]/15 backdrop-blur-sm flex items-center justify-center mb-2">
                    <b.Icon className="w-5 h-5 text-[#D4A85D]" strokeWidth={1.5} />
                  </div>
                  <p className="text-white text-xs font-semibold">{b.title}</p>
                  <p className="text-white/65 text-[10px]">{b.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== 3 STEPS ============== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="cf-steps">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-[#D4A85D] text-xs uppercase tracking-[0.25em] font-bold mb-3">{t("cf.stepsLabel")}</p>
            <h2
              className="text-3xl lg:text-4xl text-[#071A35] mb-1"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("cf.stepsTitle1")}<span className="italic">{t("cf.stepsTitle2")}</span>
            </h2>
            <div className="w-12 h-0.5 bg-[#D4A85D] mx-auto mt-3" />
          </div>

          <HowItWorksSteps />
        </div>
      </section>

      {/* ============== ADVANTAGES ============== */}
      <section className="py-16 lg:py-20 bg-white border-t border-[#071A35]/5" data-testid="cf-advantages">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-2xl lg:text-3xl text-[#071A35]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("cf.advTitle1")}<span className="italic text-[#D4A85D]">{t("cf.advTitle2")}</span>
            </h2>
            <div className="w-12 h-0.5 bg-[#D4A85D] mx-auto mt-3" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
            {ADVANTAGES.map((a, i) => (
              <div key={i} className="text-center" data-testid={`advantage-${i}`}>
                <div className="w-14 h-14 mx-auto rounded-full border border-[#D4A85D]/40 bg-[#D4A85D]/10 flex items-center justify-center mb-3">
                  <a.Icon className="w-6 h-6 text-[#D4A85D]" strokeWidth={1.5} />
                </div>
                <p className="text-[#071A35] font-bold text-xs uppercase tracking-wider mb-2">{a.title}</p>
                <p className="text-[#071A35]/60 text-xs leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA — beach panorama ============== */}
      <section className="py-10 bg-white" data-testid="cf-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl overflow-hidden relative bg-cover bg-center"
            style={{
              backgroundImage:
                "linear-gradient(90deg, rgba(7,26,53,0.95) 0%, rgba(7,26,53,0.7) 55%, rgba(7,26,53,0.2) 100%), " +
                "url(https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?w=1920&q=85)"
            }}
          >
            <div className="p-8 lg:p-12 max-w-2xl text-white">
              <h2
                className="text-3xl lg:text-4xl leading-tight mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("cf.ctaTitle1")}<span className="italic text-[#D4A85D]">{t("cf.ctaTitle2")}</span>
              </h2>
              <p className="text-white/80 text-sm lg:text-base mb-7">
                {t("cf.ctaDesc")}
              </p>

              <div className="flex flex-wrap items-center gap-6">
                <Button
                  onClick={handleBuy}
                  className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-7 py-6 text-sm uppercase tracking-wider"
                  data-testid="cf-cta-buy"
                >
                  {t("cf.ctaBuy")} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <ul className="space-y-1.5 text-sm">
                  {[t("cf.bullet1"), t("cf.bullet2"), t("cf.bullet3")].map((txt, i) => (
                    <li key={i} className="flex items-center gap-2 text-white/85">
                      <Check className="w-4 h-4 text-[#D4A85D]" strokeWidth={3} /> {txt}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
